// ⭐ Adjuntar imagen/audio/video/documento (pedido de Angelo, 2026-08-07:
// "adjuntar imagen, audio o video, junto con el texto, en las plantillas y
// en los mensajes"). Utilidad compartida entre el composer de Conversaciones
// (mensaje manual) y TemplatesModal en WorkflowsManager.js (plantilla
// reutilizable) -- misma lógica de "convertir el archivo elegido a base64 y
// detectar su tipo" en los dos lugares, una sola fuente de verdad.
//
// MAX_ATTACHMENT_BYTES tiene que coincidir con el límite real que el backend
// valida (ver MAX_TEMPLATE_MEDIA_BYTES en index.js y MAX_MEDIA_BYTES en
// baileysManager.js) -- rechazar acá ANTES de leer/subir el archivo evita
// gastar tiempo/batería del usuario en un upload que el servidor va a
// rechazar de todas formas.
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

// input[accept] -- documentos "de verdad" (pdf/doc/xls/etc) entran por la
// rama genérica de abajo (cualquier cosa que no sea image/video/audio se
// clasifica como 'document'), así que no hace falta listarlos todos acá.
export const ATTACHMENT_ACCEPT = 'image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx';

// Mapea el mimetype del archivo elegido al mismo vocabulario de tipos que ya
// usa el resto del CRM para mensajes ENTRANTES (ver extractMediaInfo en
// baileysManager.js): image/video/audio/document. Nunca devuelve 'sticker'
// ni 'gif' -- no tiene sentido mandar esos tipos desde un archivo elegido a
// mano en una plantilla o un mensaje manual.
export function detectMediaKind(mimeType) {
  const type = String(mimeType || '');
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'document';
}

// Lee un File del navegador y devuelve su contenido en base64 SIN el prefijo
// "data:<mime>;base64," que agrega FileReader.readAsDataURL -- el backend
// espera el base64 puro (mismo formato que ya usa para adjuntos entrantes,
// ver enqueueIncoming en baileysManager.js).
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
}

// Mismo propósito que fileToBase64, pero para un Blob ya descargado (no un
// File elegido del disco) -- lo usa Conversaciones.js cuando el agente
// inserta una plantilla que tiene un adjunto guardado: hay que bajar el
// binario real (GET .../message-templates/:id/media, ver TemplatesModal en
// WorkflowsManager.js para el resto del flujo de plantillas) antes de poder
// mandarlo junto con el mensaje.
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el adjunto.'));
    reader.readAsDataURL(blob);
  });
}

// Empaqueta un File elegido por el usuario en el shape que esperan los
// endpoints de plantillas/mensajes: { base64, mimetype, kind, fileName,
// sizeBytes, localPreviewUrl }. localPreviewUrl (via URL.createObjectURL) es
// SOLO para la vista previa inmediata en el navegador antes de guardar/
// enviar -- nunca se manda al backend, hay que revocarlo con
// URL.revokeObjectURL cuando ya no se necesita (ver componentes que lo usan).
export async function buildAttachmentFromFile(file) {
  if (!file) return null;
  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new Error(`El archivo supera el límite de ${Math.round(MAX_ATTACHMENT_BYTES / 1e6)}MB.`);
  }
  const base64 = await fileToBase64(file);
  return {
    base64,
    mimetype: file.type || 'application/octet-stream',
    kind: detectMediaKind(file.type),
    fileName: file.name || null,
    sizeBytes: file.size,
    localPreviewUrl: URL.createObjectURL(file),
  };
}
